// Play tab: the arcade (procedural game icons + roadmap eggs), per-game hubs
// with matches and three-mode ladders.
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';
import { drawSprite, drawEgg, petSeedFor, hashString, mulberry32, PALETTES, SPRITE_W, SPRITE_H } from './pet.js';

const html = htm.bind(h);

function Pet({ username, cell = 3 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawSprite(ref.current, petSeedFor(username), { cell });
  }, [username, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

const LIVE_GAMES = [
  { kind: 'gomoku', label: 'gomoku', blurb: 'five in a row' },
  { kind: 'connect4', label: 'connect 4', blurb: 'gravity checkers' },
  { kind: 'chess', label: 'chess', blurb: 'the royal game' },
  { kind: 'go', label: 'go', blurb: 'surround to live' },
  { kind: 'reversi', label: 'reversi', blurb: 'flip the table' },
  { kind: 'xiangqi', label: 'xiangqi', blurb: 'across the river' },
];
// the public roadmap — one egg hatches each week
const ROADMAP = ['checkers', 'hearts', 'dungeon crawl'];

// the chess cabinet is ONE oversized knight-head profile — a 12x13 hand-set horse
// silhouette ('1' ivory, '2' shadow flank) filling nearly the whole 14x14 tile.
// arched neck, squared muzzle jutting left, ear on top, a mane-notch bite on the
// back edge. the outline is stamped programmatically so it wraps ALL around.
const KN_ICON = [
  '...11.......',
  '...111......',
  '..11111.....',
  '.1111111....',
  '111111111...',
  '1111111112..',
  '11..111112..',
  '....1111112.',
  '....11111122',
  '....111122..',
  '...111111222',
  '.1111111112.',
  '.2222222222.',
];

// procedural pixel icons, 14x14 cells — zero art files, same as everything here
function drawGameIcon(canvas, kind, { cell = 3 } = {}) {
  const ctx = canvas.getContext('2d');
  const rnd = mulberry32(hashString(`vb:game:${kind}`));
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const px = (x, y, c, w = 1, hh = 1) => {
    ctx.fillStyle = c;
    ctx.fillRect(x * cell, y * cell, w * cell, hh * cell);
  };
  if (kind === 'gomoku') {
    px(1, 1, '#d9b98c', 12, 12); // wooden board
    for (let i = 0; i <= 4; i++) {
      px(2, 2 + i * 2.5 > 12 ? 12 : 2 + Math.round(i * 2.5), '#a8865f', 10, 0.34);
      px(2 + Math.round(i * 2.5), 2, '#a8865f', 0.34, 10);
    }
    // a seeded mid-game
    const spots = [[3, 3], [5, 5], [7, 5], [5, 7], [9, 7], [7, 9], [9, 3]];
    spots.forEach(([x, y], i) => {
      if (rnd() < 0.85) px(x - 0.5, y - 0.5, i % 2 ? '#f4f1ea' : '#232326', 1.6, 1.6);
    });
  } else if (kind === 'connect4') {
    px(1, 2, '#378add', 12, 11); // the blue frame
    for (let cx = 0; cx < 5; cx++)
      for (let cy = 0; cy < 4; cy++) {
        const filled = rnd() < 0.4 && cy > 0;
        const color = filled ? (rnd() < 0.5 ? '#d85a30' : '#fac775') : '#17171a';
        px(2.2 + cx * 2.4, 3.2 + cy * 2.4, color, 1.6, 1.6);
      }
  } else if (kind === 'go') {
    px(1, 1, '#c9a86a', 12, 12); // pale kaya wood
    for (let i = 0; i < 4; i++) {
      px(2.4, 2.4 + i * 3, '#96793f', 9.4, 0.34);
      px(2.4 + i * 3, 2.4, '#96793f', 0.34, 9.4);
    }
    // a corner fight: black wraps a white group
    const stones = [[2, 2, '#232326'], [5, 2, '#232326'], [2, 5, '#232326'], [5, 8, '#232326'], [5, 5, '#f4f1ea'], [8, 5, '#f4f1ea'], [8, 8, '#232326'], [11, 8, '#f4f1ea']];
    for (const [sx, sy, c] of stones) if (rnd() < 0.92) px(sx - 0.9, sy - 0.9, c, 1.9, 1.9);
  } else if (kind === 'chess') {
    // flat dark backdrop — no board, no clutter: the knight IS the icon
    px(0.5, 0.5, '#241f36', 13, 13);
    const tone = { 1: '#f4ecd8', 2: '#c9b284' };
    // dark outline all around: stamp the whole silhouette at eight sub-cell offsets
    for (const [dx, dy] of [[-0.45, 0], [0.45, 0], [0, -0.45], [0, 0.45], [-0.45, -0.45], [0.45, -0.45], [-0.45, 0.45], [0.45, 0.45]])
      for (let gy = 0; gy < 13; gy++)
        for (let gx = 0; gx < 12; gx++)
          if (KN_ICON[gy][gx] !== '.') px(1 + gx + dx, 0.5 + gy + dy, '#14111f');
    for (let gy = 0; gy < 13; gy++)
      for (let gx = 0; gx < 12; gx++) {
        const ch = KN_ICON[gy][gx];
        if (ch !== '.') px(1 + gx, 0.5 + gy, tone[ch]);
      }
    px(3.8, 3.5, '#14111f', 1.2, 1.2); // the eye
  } else if (kind === 'reversi') {
    px(1, 1, '#1f6b46', 12, 12); // deep-green felt
    for (let cx = 0; cx < 4; cx++)
      for (let cy = 0; cy < 4; cy++)
        if ((cx + cy) % 2) px(1 + cx * 3, 1 + cy * 3, '#1a5c3c', 3, 3);
    // a 2x3 cluster caught mid-flip
    const discs = [[3.5, 4.5, '#232326'], [6.5, 4.5, '#f4f1ea'], [9.5, 4.5, '#232326'], [3.5, 8.5, '#f4f1ea'], [9.5, 8.5, '#f4f1ea']];
    for (const [sx, sy, c] of discs) { // corner-cut squares read as discs at this size
      px(sx - 1, sy - 0.66, c, 2, 1.32);
      px(sx - 0.66, sy - 1, c, 1.32, 2);
    }
    px(5.5, 8.2, '#a89e8e', 2, 0.7); // the sixth disc edge-on, halfway over
  } else if (kind === 'xiangqi') {
    px(1, 1, '#c9a86a', 12, 12); // wood
    for (let i = 0; i < 5; i++) px(2, 2 + i * 2.4, '#96793f', 10, 0.34);
    for (let i = 0; i < 4; i++) px(2 + i * 3.2, 2, '#96793f', 0.34, 9.6);
    px(1, 5.8, '#d8bd86', 12, 2); // the river
    px(3, 6.5, '#b39355', 2.4, 0.4);
    px(8, 6.9, '#b39355', 2.4, 0.4);
    const dsc = (sx, sy, rim) => {
      px(sx, sy, rim, 3, 3);
      px(sx + 0.5, sy + 0.5, '#f4f1ea', 2, 2);
      px(sx + 1, sy + 0.9, rim, 1, 1.2); // glyph blob
    };
    dsc(2.4, 2.2, '#2c2738');
    dsc(7.8, 3.2, '#b8322c');
    dsc(5, 9, '#b8322c');
  } else {
    // future games get a seeded abstract cartridge
    const pal = PALETTES[Math.floor(rnd() * PALETTES.length)];
    px(2, 2, pal[0], 10, 10);
    for (let x = 0; x < 4; x++)
      for (let y = 0; y < 4; y++) if (rnd() < 0.5) px(3 + x * 2, 3 + y * 2, pal[1], 1.4, 1.4);
  }
}

function GameIcon({ kind, cell = 3 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawGameIcon(ref.current, kind, { cell });
  }, [kind, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${14 * cell} height=${14 * cell}
    style="width:${14 * cell}px;height:${14 * cell}px" />`;
}

function EggIcon({ name }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const ctx = ref.current.getContext('2d');
    ctx.globalAlpha = 0.5; // still incubating
    drawEgg(ref.current, hashString(`vb:game:${name}`), { cell: 5, crack: 0 });
    ctx.globalAlpha = 1;
  }, [name]);
  return html`<canvas ref=${ref} class="pixel" width="40" height="50" style="width:40px;height:50px" />`;
}

export function PlayTab({ me, social, onOpenGame, onProfile, onToast, version = 0 }) {
  const [hub, setHub] = useState(null); // a game kind, or null for the arcade
  const [mine, setMine] = useState(null);
  const [history, setHistory] = useState(null);
  const [record, setRecord] = useState(null); // { totals, games } from /api/games/record
  const [recordOpen, setRecordOpen] = useState(false);

  const load = () => {
    fetch('/api/games').then((r) => r.json()).then((d) => setMine(d.games ?? []));
    fetch('/api/games/history').then((r) => (r.ok ? r.json() : { history: [] })).then((d) => setHistory(d.history ?? []));
    // the record endpoint ships in a parallel lane — degrade silently until it lands
    fetch('/api/games/record').then((r) => (r.ok ? r.json() : null)).then((d) => setRecord(d)).catch(() => {});
  };
  useEffect(load, [version]);

  const recTotals = record?.totals
    ? Object.values(record.totals).reduce(
        (a, t) => ({ w: a.w + (t.w ?? 0), l: a.l + (t.l ?? 0), d: a.d + (t.d ?? 0) }),
        { w: 0, l: 0, d: 0 }
      )
    : null;

  const answerInvite = async (id, verb) => {
    const r = await fetch(`/api/games/${id}/${verb}`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (r.ok && verb === 'accept') onOpenGame(id);
    else if (!r.ok) onToast?.(d.error ?? 'invite is gone');
    load();
  };

  const yourTurn = (mine ?? []).filter((g) => g.turn_username === me.username && g.status === 'active');
  const invitesIn = (mine ?? []).filter((g) => g.status === 'invite' && g.player_b?.username === me.username);
  const invitesOut = (mine ?? []).filter((g) => g.status === 'invite' && g.player_a?.username === me.username);

  if (hub)
    return html`<${GameHub} kind=${hub} me=${me} social=${social} onToast=${onToast}
      onOpenGame=${onOpenGame} onBack=${() => { setHub(null); load(); }} />`;

  return html`<div class="metab">
    ${invitesIn.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">challenges for you</div>
      ${invitesIn.map(
        (g) => html`<div class="convrow">
          <div class="convtxt">
            <div class="convname">🎮 ${g.player_a?.username} · ${g.kind}</div>
          </div>
          <button class="mini primary" onClick=${() => answerInvite(g.id, 'accept')}>accept</button>
          <button class="mini" onClick=${() => answerInvite(g.id, 'decline')}>pass</button>
        </div>`
      )}
    </div>`}
    ${invitesOut.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">waiting on them</div>
      ${invitesOut.map(
        (g) => html`<div class="convrow">
          <div class="convtxt">
            <div class="convname dim">${g.kind} invite → ${g.player_b?.username}</div>
          </div>
          <button class="mini" onClick=${async () => { await fetch(`/api/games/${g.id}/cancel`, { method: 'POST' }); load(); }}>withdraw</button>
        </div>`
      )}
    </div>`}

    ${yourTurn.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">your move</div>
      ${yourTurn.map(
        (g) => html`<div class="convrow clickable" onClick=${() => onOpenGame(g.id)}>
          <span class="gameglyph">▶</span>
          <div class="convtxt">
            <div class="convname">${g.kind} · vs
              ${g.player_a?.username === me.username ? g.player_b?.username : g.player_a?.username}
              <span class="unreaddot"></span>
            </div>
          </div>
        </div>`
      )}
    </div>`}

    <div class="section">
      <div class="sectionhead" style="display:flex;align-items:baseline;gap:6px;flex-wrap:wrap">
        arcade <span class="dim">· pick a cabinet</span>
        ${recTotals &&
        html`<span class="dim clickable" style="margin-left:auto;cursor:pointer" onClick=${() => setRecordOpen(true)}>
          record: ${recTotals.w}W ${recTotals.l}L ${recTotals.d}D
        </span>`}
      </div>
      <div class="arcade">
        ${LIVE_GAMES.map(
          (g) => html`<div class="gamecard clickable" onClick=${() => setHub(g.kind)}>
            <${GameIcon} kind=${g.kind} />
            <div class="gamename">${g.label}</div>
            <div class="gamesub">${g.blurb}</div>
          </div>`
        )}
        ${ROADMAP.map(
          (name) => html`<div class="gamecard egg" title="hatching soon">
            <${EggIcon} name=${name} />
            <div class="gamename">${name}</div>
            <div class="gamesub">hatching soon</div>
          </div>`
        )}
      </div>
      <div class="gardenhint">one egg hatches every week — new cabinet, same arcade.</div>
    </div>

    ${history?.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">recent matches <span class="dim">· tap a name to add them</span></div>
      ${history.slice(0, 8).map(
        (h) => html`<div class="shiprow clickable" onClick=${() => onProfile?.(h.opponent)}>
          <span class="shiptitle">
            ${h.result === 'won' ? '🏆' : h.result === 'draw' ? '🤝' : '😿'} ${h.kind} vs <b>${h.opponent}</b>
          </span>
          <span class="dim">${agoShort(h.ts)}</span>
        </div>`
      )}
    </div>`}

    ${recordOpen &&
    record &&
    html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && setRecordOpen(false)}>
      <div class="zoomcard" style="width:min(92vw,380px);max-height:80vh;overflow-y:auto">
        <div class="gardenhead">
          <b>full record</b>
          <span class="closex clickable" onClick=${() => setRecordOpen(false)}>✕</span>
        </div>
        <div class="pillrow" style="margin:8px 0">
          ${Object.entries(record.totals ?? {}).map(
            ([k, t]) => html`<span class="gchip">${k} · ${t.w ?? 0}W ${t.l ?? 0}L ${t.d ?? 0}D</span>`
          )}
        </div>
        ${(record.games ?? []).map(
          (g) => html`<div class="shiprow">
            <span class="shiptitle">
              ${g.result === 'won' ? '🏆' : g.result === 'draw' ? '🤝' : '😿'} ${g.kind} vs <b>${g.opponent}</b>
              ${g.end_reason === 'timeout' ? html`<span class="dim"> · timeout</span>` : ''}
              ${g.end_reason === 'time_cap' ? html`<span class="dim"> · time cap</span>` : ''}
            </span>
            <span class="dim" style="float:right">${agoShort(g.finished_at)}</span>
          </div>`
        )}
        ${!(record.games ?? []).length && html`<div class="dim">no finished games yet — go start one.</div>`}
      </div>
    </div>`}
  </div>`;
}

function agoShort(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// one game's own page: start a match, join, and the three-board ladder
export function GameHub({ kind, me, social, onOpenGame, onBack, onToast }) {
  const [mine, setMine] = useState(null);
  const [open, setOpen] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [lmode, setLmode] = useState('all');
  const [vs, setVs] = useState('open');
  const [err, setErr] = useState('');
  const label = LIVE_GAMES.find((g) => g.kind === kind)?.label ?? kind;

  const load = () => {
    fetch('/api/games').then((r) => r.json()).then((d) => setMine((d.games ?? []).filter((g) => g.kind === kind)));
    fetch('/api/games?open=1').then((r) => r.json()).then((d) => setOpen((d.games ?? []).filter((g) => g.kind === kind)));
  };
  useEffect(load, [kind]);
  useEffect(() => {
    fetch(`/api/ladder?kind=${kind}&mode=${lmode}`).then((r) => r.json()).then(setLadder);
  }, [kind, lmode]);

  const start = async () => {
    setErr('');
    const r = await fetch('/api/games', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, opponent: vs }),
    });
    const d = await r.json();
    if (!r.ok) return setErr(d.error ?? 'failed');
    if (d.game.status === 'invite') {
      onToast?.(`invite sent to ${vs} — you'll hear the moment they accept`);
      load();
    } else onOpenGame(d.game.id);
  };

  // no rooms, no browsing: if a stranger is waiting you're in, otherwise you're next
  const quickmatch = async () => {
    setErr('');
    const r = await fetch('/api/games/quickmatch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind }),
    });
    const d = await r.json();
    if (!r.ok) return setErr(d.error ?? 'failed');
    if (d.matched) onOpenGame(d.game.id);
    else {
      onToast?.('🎲 you are in line — the next player drops right into your game');
      load();
    }
  };

  const join = async (id) => {
    const r = await fetch(`/api/games/${id}/join`, { method: 'POST' });
    if (r.ok) onOpenGame(id);
    else load();
  };

  const friends = social?.friends ?? [];

  return html`<div class="metab">
    <div class="roomhead threadhead">
      <span class="clickable" onClick=${onBack}>←</span>
      <${GameIcon} kind=${kind} cell=${2} />
      <b>${label}</b>
    </div>

    <div class="section">
      <div class="sectionhead">new match</div>
      <button class="mini primary" style="margin-bottom:8px" onClick=${quickmatch}>
        🎲 random opponent — instant if someone's waiting
      </button>
      <div class="newgame">
        <select value=${vs} onChange=${(e) => setVs(e.target.value)}>
          <option value="open">anyone (open challenge)</option>
          ${friends.map((f) => html`<option value=${f.user.username}>vs ${f.user.username} (they accept first)</option>`)}
        </select>
        <button class="mini primary" onClick=${start}>go</button>
      </div>
      ${err && html`<div class="formerr">${err}</div>`}
    </div>

    ${mine?.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">your matches</div>
      ${mine.map(
        (g) => html`<div class="convrow ${g.status === 'invite' ? '' : 'clickable'}"
          onClick=${() => g.status !== 'invite' && onOpenGame(g.id)}>
          <div class="convtxt">
            <div class="convname">
              ${g.player_a?.username} vs ${g.player_b?.username ?? '…'}
              ${g.turn_username === me.username && g.status === 'active' && html`<span class="unreaddot"></span>`}
            </div>
            <div class="convlast">${
              g.status === 'open'
                ? 'waiting for an opponent'
                : g.status === 'invite'
                  ? g.player_b?.username === me.username
                    ? 'they want to play you'
                    : 'waiting for them to accept'
                  : g.status === 'done'
                    ? 'finished'
                    : `${g.turn_username}'s turn`
            }</div>
          </div>
          ${g.status === 'invite' && g.player_b?.username === me.username &&
          html`<button class="mini primary" onClick=${async (e) => { e.stopPropagation(); await fetch(`/api/games/${g.id}/accept`, { method: 'POST' }); onOpenGame(g.id); }}>accept</button>
            <button class="mini" onClick=${async (e) => { e.stopPropagation(); await fetch(`/api/games/${g.id}/decline`, { method: 'POST' }); load(); }}>pass</button>`}
          ${(g.status === 'invite' || g.status === 'open') && g.player_a?.username === me.username &&
          html`<button class="mini" onClick=${async (e) => { e.stopPropagation(); await fetch(`/api/games/${g.id}/cancel`, { method: 'POST' }); load(); }}>withdraw</button>`}
        </div>`
      )}
    </div>`}

    ${open?.filter((g) => g.player_a?.username !== me.username).length > 0 &&
    html`<div class="section">
      <div class="sectionhead">open challenges</div>
      ${open
        .filter((g) => g.player_a?.username !== me.username)
        .map(
          (g) => html`<div class="convrow">
            <${Pet} username=${g.player_a.username} />
            <div class="convtxt">
              <div class="convname">${g.player_a.username}</div>
            </div>
            <button class="mini" onClick=${() => join(g.id)}>join</button>
          </div>`
        )}
    </div>`}

    <div class="section">
      <div class="sectionhead">ladder <span class="dim">· agents welcome, labeled</span></div>
      <div class="pillrow" style="margin-bottom:8px">
        ${['all', 'human', 'agent'].map(
          (m) => html`<span class="sizeopt wide ${lmode === m ? 'on' : ''}" onClick=${() => setLmode(m)}>
            ${m === 'all' ? 'everyone' : m === 'human' ? '🧑 humans' : '🤖 agents'}
          </span>`
        )}
      </div>
      ${ladder?.you &&
      (ladder.you.human || ladder.you.agent) &&
      html`<div class="dim" style="margin-bottom:6px">
        you:
        ${ladder.you.human && ` 🧑 ${ladder.you.human.rating} (${ladder.you.human.wins}W ${ladder.you.human.losses}L)`}
        ${ladder.you.agent && ` · 🤖 ${ladder.you.agent.rating} (${ladder.you.agent.wins}W ${ladder.you.agent.losses}L)`}
      </div>`}
      ${ladder?.top.map(
        (r) => html`<div class="ladderrow">
          <span class="rank">#${r.rank}</span>
          <${Pet} username=${r.username} />
          <span class="fname">${r.username}</span>
          <span class="dim">${r.mode === 'agent' ? '🤖' : '🧑'}</span>
          <span class="rating">${r.rating}</span>
        </div>`
      )}
      ${(!ladder || ladder.top.length === 0) && html`<div class="dim">no ranked ${label} games yet — be the first.</div>`}
    </div>
  </div>`;
}

// ---------- chess: a pseudo-3D pixel set ----------

// hand-set 8x12 piece silhouettes ('1' body, '2' shade) — same craft as the tab icons
const CH_SPRITES = {
  p: [
    '........', '........', '...11...', '..1111..', '..1112..', '...11...',
    '..1111..', '...112..', '..1112..', '..1112..', '.111112.', '11111122',
  ],
  r: [
    '........', '11.11.11', '11111112', '.111112.', '..1112..', '..1112..',
    '..1112..', '..1112..', '..1112..', '.111112.', '11111122', '11111122',
  ],
  n: [
    '...11...', '..11111.', '.1111111', '11111112', '111.1112', '11111122',
    '..111122', '..111122', '.1111122', '.1111122', '11111122', '11111122',
  ],
  b: [
    '....1...', '...111..', '...1112.', '..11112.', '..11.12.', '..1112..',
    '...112..', '..1112..', '..1112..', '.11112..', '.111122.', '11111122',
  ],
  q: [
    '.1.11.1.', '.111111.', '.111112.', '..1112..', '...112..', '..1112..',
    '..1112..', '.111122.', '.111122.', '11111122', '11111122', '11111122',
  ],
  k: [
    '...11...', '..1111..', '...11...', '..1111..', '.111112.', '..1112..',
    '..1112..', '..1112..', '.111122.', '.111122.', '11111122', '11111122',
  ],
};
const CH_PAL = {
  w: { body: '#f4ecd8', shade: '#c9b791', line: '#2c2738' },
  b: { body: '#3d3757', shade: '#282338', line: '#c2b8e0' },
};
const CH_TILE = { light: '#a8996f', dark: '#5e5680' };

function chessSq(i) {
  return 'abcdefgh'[i % 8] + (8 - Math.floor(i / 8));
}

// pseudo-legal hint targets only — the server referees checks and pins
function chessHints(board, idx) {
  const cells = board.cells;
  const p = cells[idx];
  if (!p) return [];
  const isW = (q) => q !== '' && q === q.toUpperCase();
  const white = isW(p);
  const x = idx % 8;
  const y = Math.floor(idx / 8);
  const out = [];
  const step = (cx, cy) => {
    if (cx < 0 || cx > 7 || cy < 0 || cy > 7) return false;
    const t = cells[cy * 8 + cx];
    if (t && isW(t) === white) return false;
    out.push(cy * 8 + cx);
    return t === '';
  };
  const kind = p.toUpperCase();
  if (kind === 'P') {
    const dir = white ? -1 : 1;
    const home = white ? 6 : 1;
    if (!cells[(y + dir) * 8 + x]) {
      out.push((y + dir) * 8 + x);
      if (y === home && !cells[(y + 2 * dir) * 8 + x]) out.push((y + 2 * dir) * 8 + x);
    }
    for (const dx of [-1, 1]) {
      const cx = x + dx;
      const cy = y + dir;
      if (cx < 0 || cx > 7) continue;
      const t = cy * 8 + cx;
      if ((cells[t] && isW(cells[t]) !== white) || (!cells[t] && t === board.ep)) out.push(t);
    }
  } else if (kind === 'N') {
    for (const [dx, dy] of [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]]) step(x + dx, y + dy);
  } else if (kind === 'K') {
    for (const [dx, dy] of [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]]) step(x + dx, y + dy);
    const base = white ? 56 : 0;
    const rights = board.castle ?? '';
    if (rights.includes(white ? 'K' : 'k') && !cells[base + 5] && !cells[base + 6]) out.push(base + 6);
    if (rights.includes(white ? 'Q' : 'q') && !cells[base + 1] && !cells[base + 2] && !cells[base + 3]) out.push(base + 2);
  } else {
    const dirs =
      kind === 'R'
        ? [[1, 0], [-1, 0], [0, 1], [0, -1]]
        : kind === 'B'
        ? [[1, 1], [1, -1], [-1, 1], [-1, -1]]
        : [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
    for (const [dx, dy] of dirs) {
      let cx = x + dx;
      let cy = y + dy;
      while (step(cx, cy)) {
        cx += dx;
        cy += dy;
      }
    }
  }
  return out;
}

// board geometry: rows widen and deepen toward the viewer — a stepped trapezoid
const CB_W = 352;
const CB_ROWS = (() => {
  const rows = [];
  let y = 46;
  for (let r = 0; r < 8; r++) {
    const h = 17 + r;
    const tw = 31 + Math.round((r * 9) / 7);
    rows.push({ y, h, tw, x: Math.round((CB_W - tw * 8) / 2) });
    y += h;
  }
  return rows;
})();
const CB_BOTTOM = CB_ROWS[7].y + CB_ROWS[7].h;
const CB_H = CB_BOTTOM + 26;

function drawChessPiece(ctx, letter, cx, groundY) {
  const white = letter === letter.toUpperCase();
  const grid = CH_SPRITES[letter.toLowerCase()];
  const pal = white ? CH_PAL.w : CH_PAL.b;
  const c = 2;
  const left = cx - 8;
  const top = groundY - 22;
  ctx.fillStyle = pal.line;
  for (let gy = 0; gy < 12; gy++)
    for (let gx = 0; gx < 8; gx++)
      if (grid[gy][gx] !== '.') ctx.fillRect(left + gx * c - 1, top + gy * c - 1, c + 2, c + 2);
  for (let gy = 0; gy < 12; gy++)
    for (let gx = 0; gx < 8; gx++) {
      const ch = grid[gy][gx];
      if (ch === '.') continue;
      ctx.fillStyle = ch === '2' ? pal.shade : pal.body;
      ctx.fillRect(left + gx * c, top + gy * c, c, c);
    }
}

function ChessBoard({ game, me, onMove }) {
  const ref = useRef(null);
  const [sel, setSel] = useState(-1);
  const board = game.board;
  const myNo = game.player_a?.username === me.username ? 1 : game.player_b?.username === me.username ? 2 : 0;
  const flip = myNo === 2; // black sees their own camp up front
  const disp = (i) => (flip ? 63 - i : i); // self-inverse either way
  const hints = sel >= 0 ? chessHints(board, sel) : [];

  useEffect(() => setSel(-1), [game.move_count]);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, CB_W, CB_H);
    const frame = (dr, dc, color) => {
      const { y, h, tw, x } = CB_ROWS[dr];
      ctx.fillStyle = color;
      ctx.fillRect(x + dc * tw, y, tw, 2);
      ctx.fillRect(x + dc * tw, y + h - 2, tw, 2);
      ctx.fillRect(x + dc * tw, y, 2, h);
      ctx.fillRect(x + dc * tw + tw - 2, y, 2, h);
    };
    const turnWhite = game.turn_username === game.player_a?.username;
    const checkedKing = board.check ? board.cells.indexOf(turnWhite ? 'K' : 'k') : -1;
    for (let dr = 0; dr < 8; dr++) {
      const { y, h, tw, x } = CB_ROWS[dr];
      for (let dc = 0; dc < 8; dc++) {
        const ri = disp(dr * 8 + dc);
        ctx.fillStyle = (dr + dc) % 2 === 0 ? CH_TILE.light : CH_TILE.dark;
        ctx.fillRect(x + dc * tw, y, tw, h);
        if (board.last?.includes(ri)) frame(dr, dc, '#b99a3e');
        if (ri === checkedKing) frame(dr, dc, '#d24a5e');
        if (ri === sel) frame(dr, dc, '#35d07f');
        if (hints.includes(ri)) {
          if (board.cells[ri]) frame(dr, dc, '#35d07f');
          else {
            ctx.fillStyle = '#35d07f';
            ctx.fillRect(x + dc * tw + Math.floor(tw / 2) - 3, y + Math.floor(h / 2) - 3, 6, 6);
          }
        }
      }
    }
    // front face + rank/file labels, then pieces back to front
    const front = CB_ROWS[7];
    ctx.fillStyle = '#3a3450';
    ctx.fillRect(front.x, CB_BOTTOM, front.tw * 8, 10);
    ctx.fillStyle = '#211e33';
    ctx.fillRect(front.x, CB_BOTTOM + 10, front.tw * 8, 3);
    ctx.font = '10px Silkscreen, monospace';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#8f87a8';
    for (let dc = 0; dc < 8; dc++) {
      const letter = 'abcdefgh'[flip ? 7 - dc : dc];
      ctx.fillText(letter, front.x + dc * front.tw + Math.floor(front.tw / 2) - 3, CB_BOTTOM + 14);
    }
    for (let dr = 0; dr < 8; dr++) {
      const { y, h, x } = CB_ROWS[dr];
      ctx.fillText(String(flip ? dr + 1 : 8 - dr), x - 12, y + Math.floor(h / 2) - 5);
    }
    for (let dr = 0; dr < 8; dr++) {
      const { y, h, tw, x } = CB_ROWS[dr];
      for (let dc = 0; dc < 8; dc++) {
        const p = board.cells[disp(dr * 8 + dc)];
        if (p) drawChessPiece(ctx, p, x + dc * tw + Math.floor(tw / 2), y + h - 3);
      }
    }
  }, [game, sel]);

  const onClick = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (CB_W / rect.width);
    const py = (e.clientY - rect.top) * (CB_H / rect.height);
    let di = -1;
    for (let dr = 0; dr < 8; dr++) {
      const { y, h, tw, x } = CB_ROWS[dr];
      if (py >= y && py < y + h && px >= x && px < x + tw * 8) {
        di = dr * 8 + Math.floor((px - x) / tw);
        break;
      }
    }
    if (di < 0) return setSel(-1);
    const ri = disp(di);
    if (sel >= 0 && hints.includes(ri)) {
      const m = { from: chessSq(sel), to: chessSq(ri) };
      if (board.cells[sel].toUpperCase() === 'P' && (ri < 8 || ri >= 56)) m.promote = 'q';
      setSel(-1);
      return onMove(m);
    }
    const p = board.cells[ri];
    const mine = myNo && p && (p === p.toUpperCase()) === (myNo === 1);
    setSel(mine && ri !== sel ? ri : -1);
  };

  return html`<canvas ref=${ref} class="pixel chessboard" width=${CB_W} height=${CB_H} onClick=${onClick}
    style="width:100%;max-width:${CB_W}px;image-rendering:pixelated;cursor:pointer" />`;
}

// ---------- reversi: the flip scan, mirrored from the engine for hints + the pass rule ----------

const RV_DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
function reversiMoves(cells, p) {
  const out = [];
  for (let i = 0; i < 64; i++) {
    if (cells[i]) continue;
    const x = i % 8;
    const y = (i / 8) | 0;
    for (const [dx, dy] of RV_DIRS) {
      let cx = x + dx;
      let cy = y + dy;
      let seen = 0;
      while (cx >= 0 && cx < 8 && cy >= 0 && cy < 8 && cells[cy * 8 + cx] === 3 - p) {
        seen++;
        cx += dx;
        cy += dy;
      }
      if (seen && cx >= 0 && cx < 8 && cy >= 0 && cy < 8 && cells[cy * 8 + cx] === p) {
        out.push(i);
        break;
      }
    }
  }
  return out;
}

// the felt table as a canvas — flat top-down is right for reversi, but every disc is a sprite
const RV_CELL = 40;
const RV_M = 10;
const RV_W = RV_M * 2 + 8 * RV_CELL;

// hand-set 9x9 disc at 4px a pixel: '1' body, '2' shade (lower right), '3' highlight
const RV_DISC = [
  '..11111..',
  '.1331111.',
  '133111112',
  '131111112',
  '111111122',
  '111111122',
  '111111222',
  '.1112222.',
  '..12222..',
];
const RV_PAL = {
  1: { body: '#232326', shade: '#121216', hi: '#50505c' },
  2: { body: '#f4f1ea', shade: '#c9c1ae', hi: '#ffffff' },
};

function ReversiBoard({ game, onMove, hints }) {
  const ref = useRef(null);
  const board = game.board;

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#123c26'; // table edge under the felt
    ctx.fillRect(0, 0, RV_W, RV_W);
    for (let y = 0; y < 8; y++)
      for (let x = 0; x < 8; x++) {
        ctx.fillStyle = (x + y) % 2 ? '#1a5c3c' : '#1f6b46';
        ctx.fillRect(RV_M + x * RV_CELL, RV_M + y * RV_CELL, RV_CELL, RV_CELL);
      }
    if (Number.isInteger(board.last)) {
      const x = RV_M + (board.last % 8) * RV_CELL;
      const y = RV_M + ((board.last / 8) | 0) * RV_CELL;
      ctx.fillStyle = '#f5c542';
      ctx.fillRect(x, y, RV_CELL, 2);
      ctx.fillRect(x, y + RV_CELL - 2, RV_CELL, 2);
      ctx.fillRect(x, y, 2, RV_CELL);
      ctx.fillRect(x + RV_CELL - 2, y, 2, RV_CELL);
    }
    ctx.fillStyle = '#35d07f';
    for (const t of hints) { // stepped mint dots on every legal flip
      const cx = RV_M + (t % 8) * RV_CELL + RV_CELL / 2;
      const cy = RV_M + ((t / 8) | 0) * RV_CELL + RV_CELL / 2;
      ctx.fillRect(cx - 6, cy - 2, 12, 4);
      ctx.fillRect(cx - 2, cy - 6, 4, 12);
    }
    for (let i = 0; i < 64; i++) {
      const v = board.cells[i];
      if (!v) continue;
      const pal = RV_PAL[v];
      const x0 = RV_M + (i % 8) * RV_CELL + 2;
      const y0 = RV_M + ((i / 8) | 0) * RV_CELL + 2;
      for (let gy = 0; gy < 9; gy++)
        for (let gx = 0; gx < 9; gx++) {
          const ch = RV_DISC[gy][gx];
          if (ch === '.') continue;
          ctx.fillStyle = ch === '3' ? pal.hi : ch === '2' ? pal.shade : pal.body;
          ctx.fillRect(x0 + gx * 4, y0 + gy * 4, 4, 4);
        }
    }
  }, [game, hints]);

  const cellAt = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = Math.floor(((e.clientX - rect.left) * (RV_W / rect.width) - RV_M) / RV_CELL);
    const y = Math.floor(((e.clientY - rect.top) * (RV_W / rect.height) - RV_M) / RV_CELL);
    return x >= 0 && x < 8 && y >= 0 && y < 8 ? { x, y } : null;
  };

  return html`<canvas ref=${ref} class="pixel" width=${RV_W} height=${RV_W}
    onClick=${(e) => { const c = cellAt(e); if (c) onMove({ x: c.x, y: c.y }); }}
    onMouseMove=${(e) => { const c = cellAt(e); ref.current.style.cursor = c && hints.includes(c.y * 8 + c.x) ? 'pointer' : 'default'; }}
    style="width:100%;max-width:${RV_W}px;image-rendering:pixelated" />`;
}

// ---------- xiangqi: wooden intersections and hand-set 9x9 glyph discs ----------

// 车/马/炮 share a shape across camps — 11 unique 9x9 glyphs, bold silhouettes over stroke fidelity
const XQ_MA = [ // 马: box head, long bar, hook
  '111111111', '1.......1', '1.......1', '111111111', '........1',
  '111111111', '........1', '......111', '.........',
];
const XQ_JU = [ // 车: top bar, fold, long bar, spine
  '.1111111.', '...11....', '..1.1....', '.11111111', '....1....',
  '111111111', '....1....', '....1....', '.........',
];
const XQ_PAO = [ // 炮: small 火 left, wrapped 包 right
  '.....1...', '....11111', '1.1.....1', '.1..111.1', '.1..1.1.1',
  '1.1.111.1', '1.1.1...1', '....11111', '.........',
];
const XQ_GLYPHS = {
  K: [ // 帅: tall stroke left, 巾 right
    '..1......', '.1.......', '.1..11111', '.1..1.1.1', '.1..1.1.1',
    '.1..1.1.1', '.1..1.1.1', '......1..', '......1..',
  ],
  k: [ // 将: 丬 left, 夕 over 寸 right
    '..1..111.', '1.1.1...1', '..1...1.1', '..1..1.1.', '1.1......',
    '..1.11111', '..1....1.', '..1..1.1.', '.......1.',
  ],
  A: [ // 仕: 亻 + 士
    '..1...1..', '.1....1..', '.1.111111', '.1....1..', '.1....1..',
    '.1....1..', '.1..1111.', '.1.......', '.........',
  ],
  a: [ // 士: long bar over short
    '.........', '....1....', '....1....', '111111111', '....1....',
    '....1....', '....1....', '..11111..', '.........',
  ],
  E: [ // 相: 木 + 目
    '.1...1111', '1111.1..1', '.11..1111', '11.1.1..1', '.1...1111',
    '.1...1..1', '.1...1111', '.1.......', '.........',
  ],
  e: [ // 象: crown, box, spread legs
    '...111...', '..1...1..', '.1111111.', '.1.....1.', '.1111111.',
    '....1....', '..1.1.1..', '.1..1..1.', '1...1...1',
  ],
  N: XQ_MA, n: XQ_MA, // 马
  R: XQ_JU, r: XQ_JU, // 车
  C: XQ_PAO, c: XQ_PAO, // 炮
  P: [ // 兵: 丘 over 八
    '..1......', '..111111.', '..1..1...', '..1..1...', '111111111',
    '.........', '...1.1...', '..1...1..', '.1.....1.',
  ],
  p: [ // 卒: bar, 从, cross
    '....1....', '111111111', '..1...1..', '.1.1.1.1.', '1.1...1.1',
    '....1....', '.1111111.', '....1....', '....1....',
  ],
};

// canvas runs at 2x its css size — the glyphs need the pixel density
const XQ_CELL = 68;
const XQ_M = 48;
const XQ_W = XQ_M * 2 + 8 * XQ_CELL;
const XQ_H = XQ_M * 2 + 9 * XQ_CELL;
const XQ_CSS = XQ_W / 2;
const xqX = (c) => XQ_M + c * XQ_CELL;
const xqY = (r) => XQ_M + r * XQ_CELL;
// files run a-i from red's seat; rank 1 is red's back rank (board row 9)
const xqSq = (i) => 'abcdefghi'[i % 9] + (10 - Math.floor(i / 9));

// pixelated disc: cell-quantized circle, no smooth arcs in this house
function xqDisc(ctx, cx, cy, r, color) {
  ctx.fillStyle = color;
  for (let y = -r; y <= r; y++)
    for (let x = -r; x <= r; x++)
      if (x * x + y * y <= r * r + 1) ctx.fillRect(cx + x * 4 - 2, cy + y * 4 - 2, 4, 4);
}

function drawXqPiece(ctx, letter, cx, cy) {
  const red = letter === letter.toUpperCase();
  const ink = red ? '#b8302e' : '#1d1a12';
  xqDisc(ctx, cx, cy, 6, ink); // one quantized step of rim — the glyph owns the disc
  xqDisc(ctx, cx, cy, 5, '#f4f1ea');
  const grid = XQ_GLYPHS[letter];
  ctx.fillStyle = ink;
  for (let gy = 0; gy < 9; gy++)
    for (let gx = 0; gx < 9; gx++)
      if (grid[gy][gx] === '1') ctx.fillRect(cx - 18 + gx * 4, cy - 18 + gy * 4, 4, 4);
}

// pseudo-legal targets: piece movement incl. horse-leg and cannon-screen;
// self-check (and flying-general) filtering stays with the server referee
function xqHints(cells, idx) {
  const p = cells[idx];
  if (!p) return [];
  const red = p === p.toUpperCase();
  const c = idx % 9;
  const r = (idx / 9) | 0;
  const inb = (cc, rr) => cc >= 0 && cc <= 8 && rr >= 0 && rr <= 9;
  const at = (cc, rr) => cells[rr * 9 + cc];
  const own = (t) => t !== '' && (t === t.toUpperCase()) === red;
  const out = [];
  const push = (cc, rr) => {
    if (inb(cc, rr) && !own(at(cc, rr))) out.push(rr * 9 + cc);
  };
  const ORTH = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  const kind = p.toUpperCase();
  if (kind === 'R') {
    for (const [dx, dy] of ORTH) {
      let cc = c + dx;
      let rr = r + dy;
      while (inb(cc, rr) && at(cc, rr) === '') {
        out.push(rr * 9 + cc);
        cc += dx;
        rr += dy;
      }
      if (inb(cc, rr) && !own(at(cc, rr))) out.push(rr * 9 + cc);
    }
  } else if (kind === 'C') {
    for (const [dx, dy] of ORTH) {
      let cc = c + dx;
      let rr = r + dy;
      while (inb(cc, rr) && at(cc, rr) === '') {
        out.push(rr * 9 + cc);
        cc += dx;
        rr += dy;
      }
      if (!inb(cc, rr)) continue; // no screen on this line
      cc += dx;
      rr += dy;
      while (inb(cc, rr) && at(cc, rr) === '') {
        cc += dx;
        rr += dy;
      }
      if (inb(cc, rr) && !own(at(cc, rr))) out.push(rr * 9 + cc); // capture over the screen
    }
  } else if (kind === 'N') {
    for (const [dx, dy, lx, ly] of [
      [1, 2, 0, 1], [-1, 2, 0, 1], [1, -2, 0, -1], [-1, -2, 0, -1],
      [2, 1, 1, 0], [2, -1, 1, 0], [-2, 1, -1, 0], [-2, -1, -1, 0],
    ])
      if (inb(c + lx, r + ly) && at(c + lx, r + ly) === '') push(c + dx, r + dy);
  } else if (kind === 'E') {
    for (const [dx, dy] of [[2, 2], [2, -2], [-2, 2], [-2, -2]]) {
      const cc = c + dx;
      const rr = r + dy;
      if (!inb(cc, rr)) continue;
      if (red ? rr < 5 : rr > 4) continue; // never crosses the river
      if (at(c + dx / 2, r + dy / 2) !== '') continue; // blocked eye
      push(cc, rr);
    }
  } else if (kind === 'A' || kind === 'K') {
    const steps = kind === 'A' ? [[1, 1], [1, -1], [-1, 1], [-1, -1]] : ORTH;
    for (const [dx, dy] of steps) {
      const cc = c + dx;
      const rr = r + dy;
      if (cc < 3 || cc > 5) continue; // palace walls
      if (red ? rr < 7 : rr > 2) continue;
      push(cc, rr);
    }
  } else if (kind === 'P') {
    push(c, r + (red ? -1 : 1));
    if (red ? r <= 4 : r >= 5) { // across the river: sideways unlocks, backward never
      push(c - 1, r);
      push(c + 1, r);
    }
  }
  return out;
}

function XiangqiBoard({ game, me, onMove }) {
  const ref = useRef(null);
  const [sel, setSel] = useState(-1);
  const board = game.board;
  const myNo = game.player_a?.username === me.username ? 1 : game.player_b?.username === me.username ? 2 : 0;
  const flip = myNo === 2; // black sees their own camp up front
  const disp = (i) => (flip ? (9 - ((i / 9) | 0)) * 9 + (i % 9) : i); // self-inverse either way
  const hints = sel >= 0 ? xqHints(board.cells, sel) : [];

  useEffect(() => setSel(-1), [game.move_count]);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#c9a86a';
    ctx.fillRect(0, 0, XQ_W, XQ_H);
    // the river: a quiet band with two pixel waves
    ctx.fillStyle = '#d8bd86';
    ctx.fillRect(xqX(0), xqY(4) + 4, 8 * XQ_CELL + 4, XQ_CELL - 4);
    ctx.fillStyle = '#c0a468';
    ctx.fillRect(xqX(1) + 12, xqY(4) + 28, 52, 4);
    ctx.fillRect(xqX(1) + 28, xqY(4) + 24, 16, 4);
    ctx.fillRect(xqX(5) + 20, xqY(4) + 44, 52, 4);
    ctx.fillRect(xqX(5) + 36, xqY(4) + 40, 16, 4);
    ctx.fillStyle = '#96793f';
    for (let r = 0; r < 10; r++) ctx.fillRect(xqX(0), xqY(r), 8 * XQ_CELL + 4, 4);
    for (let c = 0; c <= 8; c++) {
      if (c === 0 || c === 8) ctx.fillRect(xqX(c), xqY(0), 4, 9 * XQ_CELL + 4);
      else { // inner files break at the river
        ctx.fillRect(xqX(c), xqY(0), 4, 4 * XQ_CELL + 4);
        ctx.fillRect(xqX(c), xqY(5), 4, 4 * XQ_CELL + 4);
      }
    }
    // palace diagonals (palaces are flip-symmetric, so display space is safe)
    const diag = (c0, r0, c1, r1) => {
      const dx = Math.sign(c1 - c0);
      const dy = Math.sign(r1 - r0);
      for (let k = 0; k <= 2 * XQ_CELL; k += 4) ctx.fillRect(xqX(c0) + dx * k, xqY(r0) + dy * k, 4, 4);
    };
    diag(3, 0, 5, 2);
    diag(5, 0, 3, 2);
    diag(3, 7, 5, 9);
    diag(5, 7, 3, 9);

    const frame = (di, color) => {
      const x = xqX(di % 9) - 28;
      const y = xqY((di / 9) | 0) - 28;
      ctx.fillStyle = color;
      ctx.fillRect(x, y, 60, 4);
      ctx.fillRect(x, y + 56, 60, 4);
      ctx.fillRect(x, y, 4, 60);
      ctx.fillRect(x + 56, y, 4, 60);
    };
    const turnRed = game.turn_username === game.player_a?.username;
    const checked = board.check ? board.cells.indexOf(turnRed ? 'K' : 'k') : -1;
    if (board.last) for (const i of board.last) frame(disp(i), '#b99a3e');
    if (checked >= 0) frame(disp(checked), '#d24a5e');
    if (sel >= 0) frame(disp(sel), '#35d07f');
    for (const t of hints) {
      if (board.cells[t]) frame(disp(t), '#35d07f');
      else {
        const d = disp(t);
        ctx.fillStyle = '#35d07f';
        ctx.fillRect(xqX(d % 9) - 4, xqY((d / 9) | 0) - 4, 12, 12);
      }
    }
    for (let i = 0; i < 90; i++) {
      const p = board.cells[i];
      if (!p) continue;
      const d = disp(i);
      drawXqPiece(ctx, p, xqX(d % 9) + 2, xqY((d / 9) | 0) + 2);
    }
  }, [game, sel]);

  const onClick = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (XQ_W / rect.width);
    const py = (e.clientY - rect.top) * (XQ_H / rect.height);
    const c = Math.round((px - XQ_M - 2) / XQ_CELL);
    const r = Math.round((py - XQ_M - 2) / XQ_CELL);
    if (c < 0 || c > 8 || r < 0 || r > 9) return setSel(-1);
    const ri = disp(r * 9 + c);
    if (sel >= 0 && hints.includes(ri)) {
      const m = { from: xqSq(sel), to: xqSq(ri) };
      setSel(-1);
      return onMove(m);
    }
    const p = board.cells[ri];
    const mine = myNo && p && (p === p.toUpperCase()) === (myNo === 1);
    setSel(mine && ri !== sel ? ri : -1);
  };

  return html`<canvas ref=${ref} class="pixel" width=${XQ_W} height=${XQ_H} onClick=${onClick}
    style="width:100%;max-width:${XQ_CSS}px;image-rendering:pixelated;cursor:pointer" />`;
}

function agentPlayPrompt(game) {
  const rules =
    game.kind === 'gomoku'
      ? 'gomoku: 15x15 board, row-major cells array, move format {"x":0-14,"y":0-14}, five in a row wins.'
      : game.kind === 'chess'
      ? 'chess: full rules (castling, en passant, promotion). move format {"from":"e2","to":"e4"} plus optional "promote":"q|r|b|n" (queen default). board.cells is FEN-ordered (a8 first), uppercase = white; player 1 (the challenger) is white.'
      : game.kind === 'go'
      ? 'go: 9x9, move format {"x":0-8,"y":0-8} places a stone or {"pass":true} passes. captures/ko/suicide enforced by the server. two passes in a row end the game — area scoring, white gets 5.5 komi. player 1 (the challenger) is black.'
      : game.kind === 'reversi'
      ? 'reversi: 8x8 board, row-major cells array (0 empty, 1 black, 2 white). move format {"x":0-7,"y":0-7} — the move MUST flip at least one line of enemy discs; {"pass":true} is legal ONLY when you have no flipping move. board full or both players pass ends it — most discs wins. player 1 (the challenger) is black and moves first.'
      : game.kind === 'xiangqi'
      ? 'xiangqi (chinese chess): 9x10 board, row-major cells; uppercase letters = red, and player 1 (the challenger) is red and moves first (R chariot, N horse, E elephant, A advisor, K general, C cannon, P soldier). move format {"from":"h3","to":"e3"} — files a-i left to right from red\'s seat, ranks 1-10 counted from red\'s back rank. full rules server-enforced: horse-leg blocks, cannon captures only over one screen, palace + river limits, generals may never face on an open file; leaving your opponent with no legal move WINS.'
      : 'connect4: 7 columns x 6 rows, gravity drops pieces, move format {"col":0-6}, four in a row wins.';
  return `You are my VibeBuddy game player. Play game #${game.id} (${game.kind}) for me, in its own loop:

1. Call the vibebuddy MCP tool get_updates — if game #${game.id} is in games_waiting_your_move, it's our turn.
2. Call get_game with game_id ${game.id} to read the board. ${rules} You are "${game.player_a?.username}" (1) or "${game.player_b?.username}" (2) — check which side I am from the response.
3. Think of a strong move, then call play_move with game_id ${game.id}.
4. Pace like a human: wait 5-15 seconds between checks, never spam. The server enforces a minimum move interval — respect it.
5. Keep looping until the game ends, then tell me the result in one line.`;
}

export function GameView({ gameId, me, ws, gameEvents, onBack, onRematch }) {
  const [game, setGame] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [endgame, setEndgame] = useState(null);
  const [nowS, setNowS] = useState(() => Date.now() / 1000);
  const prevStatus = useRef(null);
  const errTimer = useRef(null);

  // the move clock ticks locally at 1s off the server's deadline timestamps —
  // every pushed game frame re-anchors it, so drift never accumulates
  const clocked = game?.status === 'active' && game?.move_deadline != null;
  useEffect(() => {
    if (!clocked) return;
    setNowS(Date.now() / 1000);
    const iv = setInterval(() => setNowS(Date.now() / 1000), 1000);
    return () => clearInterval(iv);
  }, [clocked, game?.move_deadline]);

  useEffect(() => {
    fetch(`/api/games/${gameId}`)
      .then((r) => r.json())
      .then((d) => setGame(d.game));
    ws?.send(JSON.stringify({ t: 'game_watch', game_id: gameId }));
    return () => ws?.send(JSON.stringify({ t: 'game_unwatch', game_id: gameId }));
  }, [gameId]);

  useEffect(() => {
    const last = gameEvents[gameEvents.length - 1];
    if (last && last.game.id === gameId) setGame(last.game);
  }, [gameEvents]);

  // a finished match deserves a moment, not a vanishing act
  useEffect(() => {
    if (!game) return;
    if (prevStatus.current === 'active' && game.status === 'done')
      setEndgame({ draw: !!game.draw, winner: game.winner_username, won: game.winner_username === me.username });
    prevStatus.current = game.status;
  }, [game?.status]);

  if (!game) return html`<div class="empty">…</div>`;
  const isPlayer = [game.player_a?.username, game.player_b?.username].includes(me.username);
  const myTurn = game.turn_username === me.username;
  const opponent = game.player_a?.username === me.username ? game.player_b?.username : game.player_a?.username;
  // the move clock chip: quiet until warn_at, amber ticking after, red pulse under 10s
  const clockLeft = clocked ? Math.max(0, Math.ceil(game.move_deadline - nowS)) : null;
  const clockOn = clockLeft != null && game.warn_at != null && nowS >= game.warn_at;
  const timedOutLoser =
    game.winner_username === game.player_a?.username ? game.player_b?.username : game.player_a?.username;
  const capMark = game.kind === 'go' || game.kind === 'xiangqi' ? 'hour' : 'half-hour';

  const hint = (text) => {
    setErr(text);
    clearTimeout(errTimer.current);
    errTimer.current = setTimeout(() => setErr(''), 2500);
  };

  const move = async (m) => {
    // clicks always answer back — silence reads as broken
    if (game.status === 'open') return hint('no opponent yet — your challenge is up in the arcade');
    if (game.status === 'done') return;
    if (!isPlayer) return hint("spectating — start your own match from the arcade");
    if (!myTurn) return hint(`${game.turn_username}'s move — hang tight`);
    if (busy) return;
    setBusy(true);
    setErr('');
    const r = await fetch(`/api/games/${gameId}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ move: m }),
    });
    if (!r.ok) hint((await r.json()).error ?? 'nope');
    setTimeout(() => setBusy(false), game.min_move_ms ?? 2000);
  };

  const rematch = async () => {
    if (!opponent) return;
    const r = await fetch('/api/games', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: game.kind, opponent }),
    });
    const d = await r.json();
    if (r.ok) {
      setEndgame(null);
      onRematch?.(d.game.id);
    } else hint(d.error ?? 'rematch failed');
  };

  const withdraw = async () => {
    const r = await fetch(`/api/games/${gameId}/cancel`, { method: 'POST' });
    if (r.ok) onBack();
    else hint((await r.json()).error ?? 'could not withdraw');
  };

  const board = game.board;
  const cellFor = (x, y) => board.cells[y * board.w + x];
  const stone = (v) => (v === 1 ? 'a' : v === 2 ? 'b' : '');
  // reversi niceties: the same flip scan the engine runs, for hints + the pass rule
  const rvNo = game.kind === 'reversi' ? (game.player_a?.username === me.username ? 1 : game.player_b?.username === me.username ? 2 : 0) : 0;
  const rvHints = rvNo && game.status === 'active' && myTurn ? reversiMoves(board.cells, rvNo) : [];

  return html`
    <div class="roomhead threadhead">
      <span class="clickable" onClick=${onBack}>←</span>
      <b>${game.kind}</b>
      <span class="players">
        <${Pet} username=${game.player_a?.username ?? '?'} />
        <span class=${game.turn_username === game.player_a?.username ? 'turnname' : 'dim'}>
          ${game.player_a?.username}
        </span>
        <span class="dim">vs</span>
        <${Pet} username=${game.player_b?.username ?? '?'} />
        <span class=${game.turn_username === game.player_b?.username ? 'turnname' : 'dim'}>
          ${game.player_b?.username ?? '…'}
        </span>
      </span>
    </div>
    ${game.status === 'done' &&
    html`<div class="gameover">
      ${game.end_reason === 'timeout'
        ? `${timedOutLoser} timed out — ${game.winner_username} takes it ⏱`
        : game.end_reason === 'time_cap'
          ? `a marathon — called a draw at the ${capMark} mark`
          : game.draw
            ? 'a draw — very polite'
            : `${game.winner_username} wins!`}
      <span class="mini-link" onClick=${rematch}>rematch ↻</span>
    </div>`}
    ${game.status === 'open' &&
    html`<div class="turnline">
      waiting for an opponent — your challenge is listed in the arcade
      ${game.player_a?.username === me.username &&
      html`<span class="mini-link" onClick=${withdraw}>withdraw</span>`}
    </div>`}
    ${game.status === 'active' &&
    html`<div class="turnline">
      ${myTurn ? '● your move' : `waiting for ${game.turn_username}…`}
      ${(game.kind === 'chess' || game.kind === 'xiangqi') && board?.check && html`<span style="color:#d24a5e"> · check!</span>`}
      ${clockOn &&
      html`<span style="font-weight:700;color:${clockLeft <= 10 ? '#d24a5e' : '#f5c542'};${clockLeft <= 10 ? 'animation:cblink 1s steps(2) infinite;' : ''}">
        ${' '}· ⏱ ${myTurn ? 'you' : game.turn_username}: ${clockLeft}s
      </span>`}
      ${!isPlayer && '· spectating'}
    </div>`}
    <div class="boardwrap">
      ${game.kind === 'gomoku'
        ? html`<div class="gomoku" style="grid-template-columns:repeat(${board.w},1fr)">
            ${board.cells.map(
              (v, i) => html`<div
                class="gcell ${stone(v)}"
                onClick=${() => move({ x: i % board.w, y: Math.floor(i / board.w) })}
              ></div>`
            )}
          </div>`
        : game.kind === 'chess'
        ? html`<${ChessBoard} game=${game} me=${me} onMove=${move} />`
        : game.kind === 'go'
        ? html`<div class="gomoku goban" style="grid-template-columns:repeat(${board.w},1fr)">
            ${board.cells.map(
              (v, i) => html`<div
                class="gcell ${stone(v)} ${board.last === i ? 'glast' : ''}"
                onClick=${() => move({ x: i % board.w, y: Math.floor(i / board.w) })}
              ></div>`
            )}
          </div>`
        : game.kind === 'xiangqi'
        ? html`<${XiangqiBoard} game=${game} me=${me} onMove=${move} />`
        : game.kind === 'reversi'
        ? html`<${ReversiBoard} game=${game} onMove=${move} hints=${rvHints} />`
        : html`<div class="c4" style="grid-template-columns:repeat(${board.w},1fr)">
            ${board.cells.map(
              (v, i) => html`<div class="c4cell ${stone(v)}" onClick=${() => move({ col: i % board.w })}>
                <div class="disc"></div>
              </div>`
            )}
          </div>`}
    </div>
    ${game.kind === 'go' &&
    html`<div class="turnline dim" style="display:flex;gap:10px;align-items:center">
      captures ⚫${board.caps?.[0] ?? 0} ⚪${board.caps?.[1] ?? 0}
      ${board.score && html`<span>· final ${board.score.black} — ${board.score.white} (komi in)</span>`}
      ${isPlayer && game.status === 'active' && myTurn &&
      html`<button class="mini" onClick=${() => move({ pass: true })}>
        ${board.passes > 0 ? 'pass (ends the game)' : 'pass'}
      </button>`}
    </div>`}
    ${game.kind === 'reversi' &&
    html`<div class="turnline dim" style="display:flex;gap:10px;align-items:center">
      ${game.status === 'done' ? 'final' : 'discs'}
      ⚫ ${board.cells.filter((v) => v === 1).length} · ⚪ ${board.cells.filter((v) => v === 2).length}
      ${isPlayer && game.status === 'active' && myTurn && rvHints.length === 0 &&
      html`<button class="mini" onClick=${() => move({ pass: true })}>pass — nothing flips</button>`}
    </div>`}
    ${err && html`<div class="formerr">${err}</div>`}
    ${isPlayer &&
    game.status === 'active' &&
    html`<div class="tableactions">
      <button class="mini" onClick=${async () => {
        try {
          await navigator.clipboard.writeText(agentPlayPrompt(game));
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        } catch {}
      }}>
        ${copied ? '✓ copied — paste into a fresh agent session' : '🤖 let your agent play'}
      </button>
      <button class="mini danger" onClick=${() => fetch(`/api/games/${gameId}/resign`, { method: 'POST' }).then(onBack)}>
        resign
      </button>
    </div>`}
    ${endgame &&
    html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && setEndgame(null)}>
      <div class="hatchcard">
        ${endgame.draw
          ? html`<h1 style="font-size:20px">a draw!</h1>
              <p class="dim">very polite, both of you</p>`
          : endgame.won
            ? html`<div class="hatchburst">
                  <span class="confetti c1"></span><span class="confetti c2"></span>
                  <span class="confetti c3"></span><span class="confetti c4"></span>
                  <${Pet} username=${me.username} cell=${5} />
                </div>
                <h1 style="font-size:20px">you win! 🏆</h1>
                <p class="dim">the ${game.kind} ladder felt that</p>`
            : html`<${Pet} username=${endgame.winner} cell=${5} />
                <h1 style="font-size:20px">${endgame.winner} wins</h1>
                <p class="dim">rematch and reclaim your honor?</p>`}
        <div class="pillrow" style="margin-top:10px">
          ${opponent && html`<button class="mini primary" onClick=${rematch}>rematch</button>`}
          <button class="mini" onClick=${() => setEndgame(null)}>close</button>
        </div>
      </div>
    </div>`}
  `;
}

